import type { Incident as LADSIncident } from "../../server/lads-api/clients/lcc-openapi";
import type {
  ChannelFixed,
  ChannelThresholdFixed,
} from "../api/laboperator.types.ts";
import type { ChannelIncident, ChannelIncidents } from "../schemas.ts";
import type { Device, Incident, Unit } from "../view-model.ts";

type IncidentPropertyMap = {
  [key: string]: {
    code: number;
    airflow: boolean;
    service: boolean;
  };
};
import _incidentProperties from "./incidents.json";
const incidentProperties = _incidentProperties as IncidentPropertyMap;

export function mapChannelIncidentsToIncidents(
  channelIncidents: ChannelIncidents,
  channels: { [key: string]: ChannelFixed },
  channelThresholds: { [key: string]: ChannelThresholdFixed }
): Incident[] {
  return channelIncidents
    .map((ci) => {
      const channelId = ci.relationships?.channel?.data?.id;
      const thresholdId = ci.relationships?.channel_threshold?.data?.id;
      const mappedIncident = mapChannelIncidentsToIncident(
        ci,
        (channelId && channels[channelId]) || undefined,
        (thresholdId && channelThresholds[thresholdId]) || undefined
      );
      return decomposeFumehood(mappedIncident);
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf()
    );
}

export function decomposeFumehood(i: Incident): Incident {
  // keep untouched if no can-device-channel
  if (!i.name.startsWith("can_id_")) return i;

  // extract can-id from channel to build device-id
  const canId = i.name.split("_")[2];
  const deviceId = `${i.deviceId}-${canId}`;
  // remove prefix from channel name
  const prefix = `can_id_${canId}_`;
  const name = i.name.slice(prefix.length);
  const message = i.message.replace(prefix, "");
  return {
    ...i,
    deviceId,
    name,
    message,
  };
}

/**
 * Parse the OPC-UA-flavoured "Condition is <value> and state is <State>"
 * message into structured fields. Returns null when the message doesn't
 * follow the standard limit-alarm shape (e.g. "Back to normal" on resolve).
 */
// Matches the OPC-UA-flavoured "Condition is <value> and state is <State>"
// message. The previous `[^a-zA-Z]+` between value and "state is" failed on
// the literal " and " (letters), so limitState parsing silently dropped and
// the UI fell back to showing the raw OPC-UA text.
const LIMIT_STATE_RE =
  /condition is\s+(-?\d+(?:\.\d+)?)\b.*?\bstate is (highhigh|high|lowlow|low)\b/i;

function parseLimitState(
  message: string
): { limitState: Incident["limitState"]; limitValue?: number } | null {
  const m = LIMIT_STATE_RE.exec(message);
  if (!m) return null;
  const tag = m[2]!.toLowerCase();
  let limitState: Incident["limitState"];
  switch (tag) {
    case "highhigh": {
      limitState = "HighHigh";
      break;
    }
    case "lowlow": {
      limitState = "LowLow";
      break;
    }
    case "high": {
      limitState = "High";
      break;
    }
    case "low": {
      limitState = "Low";
      break;
    }
    default: {
      return null;
    }
  }
  const value = Number(m[1]);
  return { limitState, limitValue: Number.isFinite(value) ? value : undefined };
}

export function mapLADSIncidentToIncident(
  ladsIncident: LADSIncident
): Incident {
  // Extract device ID from context path (e.g., "DeviceSet/S1-1016939/..." -> "S1-1016939")
  const contextParts = ladsIncident.context.split("/");
  const deviceId =
    contextParts.find(
      (part) =>
        part &&
        part !== "DeviceSet" &&
        part !== "FunctionalUnitSet" &&
        part !== "FunctionSet"
    ) || "unknown";

  // Map severity from LADS to view-model
  const severityMap: Record<string, "critical" | "warning"> = {
    ERROR: "critical",
    ALERT: "critical",
    WARNING: "warning",
    INFO: "warning",
    NOTICE: "warning",
  };

  // Map status from LADS to view-model. `status` describes the operator
  // lifecycle (has anyone confirmed it?), independent of whether the
  // underlying OPC-UA condition is still raised — that's `active`. List
  // filters use `active`, global rotated-error views use `state === undefined`.
  const statusMap: Record<string, "open" | "closed"> = {
    ACTIVE: "open",
    SILENCED: "open",
    ACKNOWLEDGED: "open",
    REPORTED: "open",
    CONFIRMED: "closed",
    RESOLVED: "closed",
  };

  // Determine service and airflow flags from incident type
  const typeLower = ladsIncident.type.toLowerCase();
  const service =
    typeLower.includes("service") || typeLower.includes("maintenance");
  const airflow =
    typeLower.includes("airflow") ||
    typeLower.includes("volume") ||
    typeLower.includes("pressure");

  // Extract code from string or use 0
  const code = ladsIncident.code ? Number.parseInt(ladsIncident.code) || 0 : 0;

  // Sticky operator flags from the backend (history-aware). All three are
  // independent — multiple can be true simultaneously when the operator did
  // multiple actions across the episode lifetime. Fallback to status-derived
  // heuristic when the field is absent (older backend, no history).
  // "Behoben" is derived directly from `active === false` at the consumer —
  // we do not store it, since operator audit events never resolve an alarm.
  const acknowledged =
    ladsIncident.acknowledged ?? ladsIncident.status === "ACKNOWLEDGED";
  const confirmed =
    ladsIncident.confirmed ?? ladsIncident.status === "CONFIRMED";
  const reported = ladsIncident.reported ?? ladsIncident.status === "REPORTED";

  // Extract FU name and function name from context path
  // e.g. "DeviceSet/FreezerDevice@0815/FunctionalUnitSet/FreezerUnit/FunctionSet/TemperatureController"
  //   → fuName = "FreezerUnit", functionName = "TemperatureController"
  const fuName = contextParts[3] || "";
  const functionName = contextParts.at(-1) || "";

  const parsedLimit = parseLimitState(ladsIncident.message);

  return {
    id: ladsIncident.incidentId,
    name: `${fuName}.${functionName}.${ladsIncident.type}`,
    browsePath: ladsIncident.context,
    createdAt: ladsIncident.createdAt.toISOString(),
    closedAt: ladsIncident.closedAt?.toISOString() || null,
    status: statusMap[ladsIncident.status] || "open",
    active: ladsIncident.active,
    acknowledged,
    confirmed,
    reported,
    flapCount: ladsIncident.flapCount,
    strictAudited: ladsIncident.strictAudited,
    highHighLimit: ladsIncident.highHighLimit,
    highLimit: ladsIncident.highLimit,
    lowLimit: ladsIncident.lowLimit,
    lowLowLimit: ladsIncident.lowLowLimit,
    message: ladsIncident.message,
    comment: ladsIncident.comment ?? null,
    severity: severityMap[ladsIncident.severity] || "warning",
    maxSeverity: ladsIncident.maxSeverity
      ? severityMap[ladsIncident.maxSeverity] || "warning"
      : severityMap[ladsIncident.severity] || "warning",
    code,
    help: ladsIncident.help || undefined,
    service,
    airflow,
    deviceId,
    ...parsedLimit,
  };
}

/**
 * Incidents kommen kanonisch (unter dem QUELL-Gerät). Zum Gerät gehört ein
 * Incident, wenn sein browsePath unter dem Gerät selbst liegt ODER einen der
 * (ggf. gemounteten) Channels des Geräts trifft. LADS-Geräte tragen ihre
 * Channels auf den view-units (`device.channels` bleibt dort leer, LCU-865) —
 * deshalb zählen die Unit-Channels mit. deviceId-Vergleich bleibt als
 * Fallback für Legacy-Incidents ohne browsePath.
 */
/**
 * Mount-aware ownership: an incident belongs to a device when its canonical
 * browsePath sits under the device's OWN prefix or under one of its (mounted)
 * channel/unit source roots. Monitored devices carry their mount sources in
 * `device.channels` (device-level functionSet); real devices carry them on the
 * view-units. Matched by PREFIX so an incident on a nested SubFunction or a
 * function under a whole-unit mount still attributes to the host — any depth.
 * Falls back to the deviceId for legacy incidents without a browsePath.
 */
export function deviceOwnsIncident(
  device: Device,
  incident: Incident,
  units?: Unit[]
): boolean {
  const browsePath = incident.browsePath;
  if (!browsePath) return incident.deviceId === device.id;
  if (browsePath.startsWith(`DeviceSet/${device.id}/`)) return true;
  const mountRoots = [
    ...Object.values(device.channels ?? {}),
    ...(units ?? []).flatMap((unit) => Object.values(unit.functions ?? {})),
  ]
    .map((c) => c?.browsePath)
    .filter(Boolean) as string[];
  return mountRoots.some(
    (root) => browsePath === root || browsePath.startsWith(`${root}/`)
  );
}

/**
 * Find the device an incident should DISPLAY under (mount-aware). Prefers the
 * device that OWNS the incident's canonical browsePath — so a monitored device
 * shows the incidents mounted onto it instead of the raw (often room-invisible)
 * source device, which otherwise renders as "Unbekanntes Modell" and blocks the
 * ack/report actions (undefined device). Falls back to the naive deviceId match.
 */
export function resolveIncidentDevice(
  incident: Incident,
  devices: Device[],
  getUnits?: (deviceId: string) => Unit[]
): Device | undefined {
  const owner = devices.find((device) =>
    deviceOwnsIncident(device, incident, getUnits?.(device.id))
  );
  return owner ?? devices.find((device) => device.id === incident.deviceId);
}

export function getIncidentsForDevice(
  device: Device,
  incidents: Incident[],
  units?: Unit[]
) {
  const deviceIncidents = {
    errors: [] as Incident[],
    warnings: [] as Incident[],
  };

  for (const incident of incidents) {
    if (
      deviceOwnsIncident(device, incident, units) &&
      incident.active !== false
    ) {
      if (incident.severity === "critical") {
        deviceIncidents.errors.push(incident);
      } else {
        deviceIncidents.warnings.push(incident);
      }
    }
  }

  return deviceIncidents;
}

/**
 * Aggregate error/warning counts across devices — the room-overview rollup.
 * MUST resolve each device's view-units (`getUnits`) so incidents on MOUNTED
 * FUs/functions (whose browsePath sits on the SOURCE device) are attributed to
 * the host, exactly like the detail view. `getUnits` is required so a caller
 * cannot silently drop mount-awareness (the bug this guards against).
 */
export function getIncidentCountForDevices(
  devices: Device[],
  incidents: Incident[],
  getUnits: (deviceId: string) => Unit[]
): { errors: number; warnings: number } {
  const count = { errors: 0, warnings: 0 };
  for (const device of devices) {
    const deviceIncidents = getIncidentsForDevice(
      device,
      incidents,
      getUnits(device.id)
    );
    count.errors += deviceIncidents.errors.length;
    count.warnings += deviceIncidents.warnings.length;
  }
  return count;
}

/**
 * Build a partial Incident view-model from an `IncidentChanged` WebSocket
 * payload. Used for optimistic SWR-cache inserts on `active=true` events so
 * the UI can react without a full /incidents refetch.
 *
 * The result lacks the audit-history fields (acknowledged/confirmed/reported)
 * — they default to `false` for fresh alarms. If the WS event was itself an
 * audit-action, the caller patches the existing cache entry instead of using
 * this mapper.
 */
export function mapIncidentChangedToIncident(msg: {
  browsePath: string;
  active: boolean;
  severity?: number;
  incidentId?: string;
  createdAt?: string;
  message?: string;
  limitState?: string;
  severityLevel?: "ERROR" | "ALERT" | "WARNING" | "INFO" | "NOTICE";
  maxSeverityLevel?: "ERROR" | "ALERT" | "WARNING" | "INFO" | "NOTICE";
}): Incident | null {
  // browsePath: DeviceSet/<device>/FunctionalUnitSet/<fu>/FunctionSet/<func>
  const parts = msg.browsePath.split("/");
  const deviceId = parts[1];
  const fuName = parts[3] ?? "";
  const funcName = parts[5] ?? parts.at(-1) ?? "";
  if (!deviceId || !msg.incidentId || !msg.createdAt) return null;

  // Backend pre-computed severityLevel → view-model severity (ERROR/ALERT →
  // critical, sonst warning). Fehlt es (legacy event), Fallback auf numeric.
  const levelToSeverity = (
    level: typeof msg.severityLevel
  ): Incident["severity"] | undefined => {
    if (level === "ERROR" || level === "ALERT") return "critical";
    return level ? "warning" : undefined;
  };

  const severity: Incident["severity"] =
    levelToSeverity(msg.severityLevel) ??
    ((msg.severity ?? 0) >= 667 ? "critical" : "warning");
  // maxSeverity = SCHÄRFSTER Grad der Episode, vom Backend im WS-Contract
  // (maxSeverityLevel). Fallback auf den aktuellen severity.
  const maxSeverity: Incident["severity"] =
    levelToSeverity(msg.maxSeverityLevel) ?? severity;

  const parsedLimit = msg.message ? parseLimitState(msg.message) : null;

  return {
    id: msg.incidentId,
    name: `${fuName}.${funcName}.${msg.limitState ?? "alarm"}`,
    browsePath: msg.browsePath,
    createdAt: msg.createdAt,
    closedAt: null,
    status: "open",
    active: msg.active,
    acknowledged: false,
    confirmed: false,
    reported: false,
    message: msg.message ?? "",
    comment: null,
    severity,
    maxSeverity,
    code: 0,
    help: undefined,
    service: false,
    airflow: false,
    deviceId,
    ...parsedLimit,
  };
}

/**
 * Apply an IncidentChanged WS event to the current activeIncidents list.
 *
 * Pure — no side effects, no hooks. Returns the new list or "refetch" when
 * the WS payload is too sparse to construct an incident locally.
 *
 * FU-mapping detail: backend `getIncidents` expands one underlying alarm into
 * multiple Incident rows (same `id`, different `deviceId`/`name`) when its
 * FunctionalUnit is mounted under another device via Organizes. The WS event
 * arrives only for the original path. We match by `id` first so all aliases
 * are updated together; path-prefix is the legacy fallback.
 */
export type IncidentChangePayload = {
  browsePath: string;
  active: boolean;
  auditAction?: "ack" | "confirm" | "report";
  incidentId?: string;
  createdAt?: string;
  message?: string;
  severity?: number;
  limitState?: string;
  severityLevel?: "ERROR" | "ALERT" | "WARNING" | "INFO" | "NOTICE";
  maxSeverityLevel?: "ERROR" | "ALERT" | "WARNING" | "INFO" | "NOTICE";
};

export function applyIncidentChange(
  current: Incident[],
  data: IncidentChangePayload
): Incident[] | "refetch" {
  // browsePath is the matching key (REST `context` and WS browsePath carry the
  // same function-level path, alias-expanded consistently) — `name` is a label.
  const matchesPath = (i: Incident): boolean =>
    i.browsePath === data.browsePath;
  const matches = (i: Incident): boolean =>
    data.incidentId ? i.id === data.incidentId : matchesPath(i);

  if (data.active === false) {
    // Inactive condition → drop every row of this alarm. The backend fires the
    // resolve ONCE, for the original browsePath, but an organized-FU mount
    // alias-expands the SAME alarm instance (same canonical id) into sibling
    // rows under other browsePaths — those must go too, or they stick until F5.
    // Match by id OR browsePath: id catches the alias rows; browsePath is the
    // fallback when the WS id doesn't match the REST row (a backend restart can
    // fabricate an id the FE never saw — see apply-incident-change.spec.ts).
    return current.filter(
      (i) => i.id !== data.incidentId && i.browsePath !== data.browsePath
    );
  }

  if (data.auditAction) {
    let flagKey: keyof Incident = "reported";
    if (data.auditAction === "ack") {
      flagKey = "acknowledged";
    } else if (data.auditAction === "confirm") {
      flagKey = "confirmed";
    }
    return current.map((i) => (matches(i) ? { ...i, [flagKey]: true } : i));
  }

  const fresh = mapIncidentChangedToIncident(data);
  if (!fresh) return "refetch";
  const others = current.filter((i) => i.browsePath !== fresh.browsePath);
  return [fresh, ...others];
}

export function mapChannelIncidentsToIncident(
  ci: ChannelIncident,
  channel?: ChannelFixed,
  channelThreshold?: ChannelThresholdFixed
): Incident {
  const channelName = channel?.attributes?.name || "undefined";
  let incidentProps;

  const errorType = channelName.split("_").slice(6).join("_");

  if (errorType && incidentProperties[errorType]) {
    incidentProps = incidentProperties[errorType];
  }

  // extract can-id from channel to build device-id
  return {
    createdAt: ci.attributes?.created_at || new Date().toISOString(),
    closedAt: ci.attributes?.closed_at || null,
    status: ci.attributes?.closed_at ? "closed" : "open",
    deviceId:
      channel?.relationships?.device?.data?.id?.toString() || "undefined",
    id: (ci.id || 0).toString(10),
    name: channel?.attributes?.name || "undefined",
    code: incidentProps?.code || 255,
    message: `${errorType}.message`,
    help: `${errorType}.help`,
    service: incidentProps?.service || true,
    airflow: incidentProps?.airflow || false,
    severity: channelThreshold?.attributes.level || "critical",
    channelId: channel?.id,
    threshold: channelThreshold,
  };
}
